import * as RadixToast from '@radix-ui/react-toast';
import cx from 'classnames';
import { useSyncExternalStore } from 'react';
import { DEFAULT_TOAST_DURATION, Toast } from './Toast';
import styles from './Toast.module.css';
import { closeToast, getToasts, subscribeToToasts } from './toastStore';

// ─── Types ─────────────────────────────────────────────────────────────────

type ToasterProps = {
  /** Test identifier, applied to the viewport list */
  dataTestId?: string;
  /** Additional CSS class on the viewport — use it to move or resize the stack */
  className?: string;
  /** Announced by screen readers before each toast */
  label?: string;
};

export type { ToasterProps };

// ─── Helpers ───────────────────────────────────────────────────────────────

function toRadixDuration(seconds = DEFAULT_TOAST_DURATION) {
  // Radix treats both 0 and Infinity as "never auto-dismiss"; ours is 0, like antd's.
  return seconds <= 0 ? Number.POSITIVE_INFINITY : seconds * 1000;
}

// ─── Component ─────────────────────────────────────────────────────────────

/**
 * Renders every toast opened through the `toast` singleton. Mount exactly one,
 * once, at the app root — it portals into a fixed viewport, so where it sits in
 * the tree only matters for the i18n and theme providers around its content.
 *
 * Radix owns the parts that are easy to get wrong: the live region, pausing
 * every timer while the stack is hovered or focused, F8 to jump into the stack,
 * Escape and swipe-right to dismiss.
 */
export function Toaster(props: ToasterProps) {
  const { dataTestId = 'toaster', className, label = 'Notification' } = props;

  const entries = useSyncExternalStore(subscribeToToasts, getToasts, getToasts);

  return (
    <RadixToast.Provider label={label} swipeDirection="right">
      {entries.map((entry) => (
        // The key carries the version so that re-firing an id remounts the
        // toast, which is what restarts its auto-dismiss timer.
        <RadixToast.Root
          key={`${entry.id}-${entry.version}`}
          className={styles.item}
          duration={toRadixDuration(entry.duration)}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              closeToast(entry.id);
            }
          }}
        >
          <Toast
            dataTestId={entry.dataTestId}
            variant={entry.variant}
            message={entry.message}
            description={entry.description}
            action={entry.action}
            showProgress={entry.showProgress}
            duration={entry.duration}
            width={entry.width}
            className={entry.className}
            onClose={() => closeToast(entry.id)}
          />
        </RadixToast.Root>
      ))}
      <RadixToast.Viewport className={cx(styles.viewport, className)} data-testid={dataTestId} />
    </RadixToast.Provider>
  );
}
